import styled from 'styled-components'
import { conditionFields, evaluateCondition } from "../../../../common/model/condition/evaluate"
import { useState } from 'react'
import useTranslation from '../../hooks/useTranslation'
import RButton from '../generic/RButton'
import ModalForm from '../modal/ModalForm'
import resolveModalForm from '../../utils/model/resolveModalForm'


const ActionLineActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`
const ActionLineContainer = styled.div`
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 0.5rem;
`


/**
 * Check if a condition is met
 * Supports both single condition object and array of conditions
 * 
 * @param {object|array} condition - Condition(s) to check
 * @param {object} data - Data object to check against
 * @returns {boolean} True if all conditions are met
 */
const checkCondition = (condition, data) => {
    // Missing value counts as false — see NotificationBar for why this coercion
    // lives at the call site rather than in the shared evaluator.
    const values = {}
    for (const f of conditionFields(condition)) {
        values[f] = data?.[f] === undefined ? false : data[f]
    }
    return evaluateCondition(condition, { values })
}



/**
 * ActionLines Component
 * 
 * Displays conditional action lines with buttons that can open modals.
 * 
 * Props:
 * - actions: Array of action configurations with conditions and line definitions
 * - masterData: The master entity data (e.g., invoice) for condition checking and merge fields
 * - detailData: The detail entity data for merge fields (fallback)
 * - useStore: Store hook for modal state management
 * - onUpdate: Callback to refresh data after modal actions
 */
const ActionLines = ({ actions, masterData, detailData, useStore, onUpdate }) => {
    const { t } = useTranslation()
    const [showModal, setShowModal] = useState(false)
    const [selectedAction, setSelectedAction] = useState(null)
    
    if (!actions || actions.length === 0) return null
    
    // Filter actions based on conditions using masterData
    const validActions = actions.filter(actionConfig => {
        return checkCondition(actionConfig.condition, masterData || {})
    })
    
    if (validActions.length === 0) return null
    
    // Handle action button click
    const handleActionClick = (actionConfig) => {
        const button = actionConfig.line?.actions?.[0]?.button
        if (button?.actionType === 'showModal') {
            setSelectedAction(actionConfig)
            setShowModal(true)
        }
    }
    
    // Handle modal close
    const handleModalClose = () => {
        setShowModal(false)
        setSelectedAction(null)
    }
    
    // Handle update - refresh queries
    const handleUpdate = async () => {
        // Call onUpdate callback if provided (from MasterDetailView)
        if (typeof onUpdate === 'function') {
            // Pass null to signal a simple refresh
            await onUpdate(null, null, null)
        }
    }
    
    // Get modal groups for the selected action
    const getModalForm = () => {
        const actionButton = selectedAction?.line?.actions?.[0]?.button
        return resolveModalForm(actionButton)
    }
    
    // Context item for merge fields - prefer masterData, fallback to detailData
    const contextItem = masterData || detailData || {}
    
    return (
        <>
            {validActions.map((actionConfig, actionIndex) => {
                const lineConfig = actionConfig.line
                if (!lineConfig) return null
                
                return (
                    <ActionLineContainer key={actionIndex}>
                        {lineConfig.actions && lineConfig.actions.length > 0 && (
                            <ActionLineActions>
                                {lineConfig.actions.map((action, btnIndex) => (
                                    <RButton
                                        key={btnIndex}
                                        label={action.label}
                                        icon={action.icon}
                                        $variant={action.button?.variant || 'secondary'}
                                        $shadow={false}
                                        $brightBackground={action.button?.$brightBackground || false}
                                        onClick={() => handleActionClick(actionConfig)}
                                    >
                                        {t(action.label)}
                                    </RButton>
                                ))}
                            </ActionLineActions>
                        )}
                    </ActionLineContainer>
                )
            })}
            
            {/* Modal for actions */}
            {showModal && selectedAction && (
                <ModalForm
                    name='ActionModal'
                    mode='fullscreen'
                    isVisible={showModal}
                    setIsVisible={setShowModal}
                    modalTitle={selectedAction.line?.actions?.[0]?.button?.label || 'Action'}
                    id={getModalForm().id}
                    groups={getModalForm().elements}
                    buttons={getModalForm().buttons}
                    submit={getModalForm().submit}
                    useStore={useStore}
                    parentId={masterData?.id}
                    testId={`${masterData?.entity}.action.${selectedAction.line?.actions?.[0]?.button?.action}`}
                    onUpdate={handleUpdate}
                    onClose={handleModalClose}
                />
            )}
        </>
    )
}

export default ActionLines
